import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Are map, reduce, and filter turing complete?",
  "description": "",
  "published": "2013-09-17T00:00:00.000Z",
  "redirect_from": ["/blog/are-map-reduce-and-filter-turing-complete/swizec/6341"],
  "categories": "Functional programming, Haskell, JavaScript, Lisp, Recursion, Turing completeness, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Yes.`}</p>
    <p>{`You can implement any algorithm with map, reduce, and filter when taken in the context of modern languages that implement these constructs. The predicate functions they rely on are turing complete, which makes them turing complete by extension.`}</p>
    <p>{`But that's smartass answer isn't it? Implementing your whole algorithm in a predicate function and wrapping it in a map call feels like cheating.`}</p>
    <p>{`Can you implement an algorithm `}<em parentName="p">{`just`}</em>{` with map, reduce, and filter?`}</p>
    <p>{`No.`}</p>
    <p>{`Those three functions on their own can't do squat. Writing something like `}<inlineCode parentName="p">{`map(reduce(reduce(map(reduce(filter(4))))))`}</inlineCode>{` will not make anything happen.`}</p>
    <p>{`We first have to decide what we're trying to ask.`}</p>
    <p>{`I've been losing sleep over this whole thing ever since a technical editor nerdsniped me last week. `}<em parentName="p">{`"A smart man once told me you can write any program as a sequence of map and reduce calls. I like to add filter for a more comprehensive toolset."`}</em>{`, I wrote in `}<a parentName="p" {...{
        "href": "http://www.packtpub.com/data-visualization-with-d3js/book"
      }}>{`Data Visualization with d3.js`}</a>{`.`}</p>
    <p>{`The editor's only comment was `}<em parentName="p">{`"This is wrong. For instance, you can't implement sorting like this."`}</em></p>
    <p>{`And well, yes, he's right. But he also isn't. What did Smart Guy (tm) try to tell me all those years ago when he introduced me to functional programming?`}</p>
    <p>{`Back to basics.`}</p>
    <p>{`To have a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Turing_completeness",
        "title": "Turing completeness"
      }}>{`turing complete language`}</a>{` you need:`}</p>
    <ol>
      <li parentName="ol">{`Write cell`}</li>
      <li parentName="ol">{`Read cell`}</li>
      <li parentName="ol">{`Move left`}</li>
      <li parentName="ol">{`Move right`}</li>
      <li parentName="ol">{`Branching (based on cell value)`}</li>
    </ol>
    <p>{`We assume our memory is practically infinite. Meaning it's not infinite per se, but we can add more when we run out.`}</p>
    <p>{`In his `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/the-birth-of-lisp-a-summary-of-john-mccarthys-original-paper/swizec/5075"
      }}>{`LISP-creating paper, McCarthy wrote`}</a>{` that a recursive alternative to an iterative turing machine needs the following constructs:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`atom`}</inlineCode>{` - is this symbol an atom (one of the basic characters)`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`eq`}</inlineCode>{` - are both these symbols atoms`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`car`}</inlineCode>{` - returns the first part of a two-element list`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`cdr`}</inlineCode>{` - returns the second part of a two-element list`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`cons`}</inlineCode>{` - constructs a list from two elements`}</li>
      <li parentName="ol">{`recursive definitions using these five constructs`}</li>
    </ol>
    <p>{`Where does this leave us in regards to our original question? `}<em parentName="p">{`Can`}</em>{` we construct anything out of a sequence of map, reduce, and filter?`}</p>
    <p>{`It depends.`}</p>
    <p>{`In the context of a strict `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`functional language`}</a>{`, with the addition of recursion, yes we probably can.`}</p>
    <p>{`After all, implementing a naive `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Quicksort",
        "title": "Quicksort"
      }}>{`quicksort`}</a>{` in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{` takes nothing more than recursion, `}<inlineCode parentName="p">{`car`}</inlineCode>{`, `}<inlineCode parentName="p">{`cdr`}</inlineCode>{`, `}<inlineCode parentName="p">{`cons`}</inlineCode>{` and filter.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`quicksort [] = []
quicksort (x:xs) =
          let smaller = quicksort (filter (<=x) xs)
              bigger = quicksort (filter (>x) xs)
          in smaller ++ [x] ++ bigger
`}</code></pre>
    <p>{`Basically, if `}<inlineCode parentName="p">{`quicksort`}</inlineCode>{` gets an empty list, return an empty list. Otherwise make a new list and put elements smaller than `}<inlineCode parentName="p">{`x`}</inlineCode>{` on left, bigger on right. `}<inlineCode parentName="p">{`++`}</inlineCode>{` is essentially cons and that `}<inlineCode parentName="p">{`(x:xs)`}</inlineCode>{` bit splits the list so the first item becomes `}<inlineCode parentName="p">{`x`}</inlineCode>{` and the other items become `}<inlineCode parentName="p">{`xs`}</inlineCode>{`.`}</p>
    <p>{`One of the tersest sorting implementations I've ever seen. And we didn't `}<em parentName="p">{`really`}</em>{` go out of our map, reduce, filter framework did we? The ability to append to and split lists is usually assumed.`}</p>
    <p>{`But can we go a step further? Can we implement `}<inlineCode parentName="p">{`filter`}</inlineCode>{` with map and reduce? Let's try.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function filter(predicate, list) {
  return list
    .map(function (a) {
      return predicate(a) ? [a] : null;
    })
    .reduce(function (prev, current) {
      return current ? prev.concat(current) : prev;
    }, []);
}
`}</code></pre>
    <p>{`Yes we can. Even in Javascript, which the original question was about. It's rather simple - mark unwanted entries with `}<inlineCode parentName="p">{`map`}</inlineCode>{`, then use `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` to create a new list sans unwanted entries.`}</p>
    <p>{`We could go a step further and use only `}<inlineCode parentName="p">{`reduce`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function filter2(predicate, list) {
  return list.reduce(function (prev, current) {
    return predicate(current) ? prev.concat(current) : prev;
  }, []);
}
`}</code></pre>
    <p>{`Turns out `}<inlineCode parentName="p">{`reduce`}</inlineCode>{` and `}<inlineCode parentName="p">{`filter`}</inlineCode>{` are the same function once you assume branching and comparison are a given. `}<inlineCode parentName="p">{`.concat`}</inlineCode>{` is Javascript's version of cons by the way.`}</p>
    <p>{`We can implement that Haskell example in `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`Javascript`}</a>{` as well. Proving that even with Javascript recursion, map, and reduce are enough to do anything.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function quicksort(list) {
  if (list.length == 0) {
    return [];
  }

  var x = list[0],
    xs = list.slice(1);

  return quicksort(
    filter2(function (a) {
      return a <= x;
    }, xs)
  )
    .concat(x)
    .concat(
      quicksort(
        filter2(function (a) {
          return a > x;
        }, xs)
      )
    );
}
`}</code></pre>
    <p>{`Exact same code as before, just wordier because Javascript is fluffier than Haskell.`}</p>
    <p>{`But are we any closer to discovering our question?`}</p>
    <p>{`Not really no. We've just shown that given conditionals, a way to slice and append lists, recursion, map, and reduce we can do anything. Even in Javascript.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=5af887dc-447d-4f5a-98dc-08047cfee16f",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      